/**
 * HOA Special Assessments Component
 * Create, manage, and track special assessments for property repairs/improvements
 */
import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  DollarSign, Plus, CheckCircle, Clock, AlertTriangle, Building2,
  FileText, Loader2, ChevronRight, Users, Percent
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOAAssessments = ({ orgId }) => {
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showPayment, setShowPayment] = useState(null);

  const [newAssessment, setNewAssessment] = useState({
    title: '', description: '', category: 'repair', total_amount: '', due_date: ''
  });
  const [paymentForm, setPaymentForm] = useState({ amount: '', method: 'check', reference: '' });

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  useEffect(() => { fetchAssessments(); }, [orgId]);

  const fetchAssessments = async () => {
    setLoading(true);
    try {
      const res = await api.get(`${API}/hoa/assessments?organization_id=${orgId || ''}`, { headers: getHeaders() });
      setAssessments(res.data.assessments || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const createAssessment = async () => {
    try {
      const res = await api.post(`${API}/hoa/assessments`, {
        ...newAssessment, organization_id: orgId, total_amount: parseFloat(newAssessment.total_amount)
      }, { headers: getHeaders() });
      toast.success('Assessment created');
      setShowCreate(false);
      setNewAssessment({ title: '', description: '', category: 'repair', total_amount: '', due_date: '' });
      fetchAssessments();
    } catch (e) { toast.error('Failed to create assessment'); }
  };

  const approveAssessment = async (id) => {
    try {
      await api.post(`${API}/hoa/assessments/${id}/approve`, {}, { headers: getHeaders() });
      toast.success('Assessment approved');
      fetchAssessments();
      if (selectedAssessment?.id === id) refreshSelected(id);
    } catch (e) { toast.error('Failed to approve'); }
  };

  const recordPayment = async (assessmentId, unitId) => {
    try {
      await api.post(`${API}/hoa/assessments/${assessmentId}/payment`, {
        unit_id: unitId, amount: parseFloat(paymentForm.amount),
        method: paymentForm.method, reference: paymentForm.reference
      }, { headers: getHeaders() });
      toast.success('Payment recorded');
      setShowPayment(null);
      setPaymentForm({ amount: '', method: 'check', reference: '' });
      refreshSelected(assessmentId);
    } catch (e) { toast.error('Failed to record payment'); }
  };

  const refreshSelected = async (id) => {
    try {
      const res = await api.get(`${API}/hoa/assessments/${id}`, { headers: getHeaders() });
      setSelectedAssessment(res.data.assessment);
    } catch (e) { console.error(e); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(v || 0);

  const statusBadge = (s) => {
    const m = {
      pending_approval: 'bg-yellow-100 text-yellow-800', approved: 'bg-blue-100 text-blue-800',
      active: 'bg-green-100 text-green-800', completed: 'bg-gray-100 text-gray-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={`${m[s] || 'bg-gray-100'} text-xs`}>{s?.replace('_', ' ')}</Badge>;
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-emerald-500" /></div>;

  // Detail View
  if (selectedAssessment) {
    const a = selectedAssessment;
    const units = a.unit_assessments || [];
    const totalPaid = units.reduce((s, u) => s + (u.paid || 0), 0);
    const paidCount = units.filter(u => u.status === 'paid').length;

    return (
      <div className="space-y-4" data-testid="assessment-detail">
        <Button variant="ghost" size="sm" onClick={() => setSelectedAssessment(null)} className="text-xs">&larr; Back</Button>
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-bold">{a.title}</h3>
            <p className="text-sm text-gray-600">{a.description}</p>
            <div className="flex items-center gap-2 mt-1">{statusBadge(a.status)}<Badge variant="outline" className="text-xs capitalize">{a.category}</Badge></div>
          </div>
          {a.status === 'pending_approval' && (
            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-xs" onClick={() => approveAssessment(a.id)} data-testid="approve-assessment-btn">
              <CheckCircle className="h-3 w-3 mr-1" /> Approve Assessment
            </Button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="p-3">
              <p className="text-[10px] text-blue-600 font-medium">Total Amount</p>
              <p className="text-base sm:text-lg font-bold text-blue-700">{formatCurrency(a.total_amount)}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200">
            <CardContent className="p-3">
              <p className="text-[10px] text-emerald-600 font-medium">Collected</p>
              <p className="text-base sm:text-lg font-bold text-emerald-700">{formatCurrency(totalPaid)}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200">
            <CardContent className="p-3">
              <p className="text-[10px] text-amber-600 font-medium">Per Unit</p>
              <p className="text-base sm:text-lg font-bold text-amber-700">{formatCurrency(a.per_unit_amount)}</p>
            </CardContent>
          </Card>
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="p-3">
              <p className="text-[10px] text-purple-600 font-medium">Paid / Total</p>
              <p className="text-base sm:text-lg font-bold text-purple-700">{paidCount}/{units.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Progress Bar */}
        <Card>
          <CardContent className="p-3">
            <div className="flex justify-between text-xs text-gray-600 mb-1">
              <span>Collection Progress</span>
              <span>{a.total_amount > 0 ? Math.round((totalPaid / a.total_amount) * 100) : 0}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div className="bg-emerald-500 h-3 rounded-full transition-all" style={{ width: `${a.total_amount > 0 ? Math.min((totalPaid / a.total_amount) * 100, 100) : 0}%` }} />
            </div>
          </CardContent>
        </Card>

        {/* Unit Breakdown */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center"><Building2 className="h-4 w-4 mr-2 text-blue-600" />Unit Assessment Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="grid grid-cols-5 text-xs font-medium text-gray-500 pb-1 border-b">
                <span>Unit</span><span>Owner</span><span className="text-right">Amount</span><span className="text-right">Paid</span><span className="text-right">Status</span>
              </div>
              {units.map((u, i) => (
                <div key={i} className="grid grid-cols-5 items-center text-xs sm:text-sm py-1.5 border-b border-gray-100">
                  <span className="font-medium">{u.unit_number}</span>
                  <span className="text-gray-600 truncate">{u.owner_name}</span>
                  <span className="text-right">{formatCurrency(u.amount)}</span>
                  <span className="text-right text-emerald-600">{formatCurrency(u.paid)}</span>
                  <div className="flex items-center justify-end gap-1">
                    <Badge className={`text-[10px] ${u.status === 'paid' ? 'bg-green-100 text-green-700' : u.status === 'partial' ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                      {u.status}
                    </Badge>
                    {u.status !== 'paid' && (
                      <Button size="sm" variant="ghost" className="h-6 px-1 text-xs" onClick={() => { setShowPayment(u.unit_id); setPaymentForm({ amount: String(u.amount - (u.paid || 0)), method: 'check', reference: '' }); }}>
                        <DollarSign className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Payment Dialog */}
        <Dialog open={!!showPayment} onOpenChange={() => setShowPayment(null)}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle className="text-base">Record Payment</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-xs">Amount</Label><Input type="number" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm({...paymentForm, amount: e.target.value})} /></div>
              <div><Label className="text-xs">Method</Label>
                <Select value={paymentForm.method} onValueChange={v => setPaymentForm({...paymentForm, method: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="check">Check</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Reference #</Label><Input value={paymentForm.reference} onChange={e => setPaymentForm({...paymentForm, reference: e.target.value})} placeholder="Check # or transaction ID" /></div>
            </div>
            <DialogFooter><Button size="sm" onClick={() => recordPayment(selectedAssessment.id, showPayment)}>Record Payment</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // List View
  return (
    <div className="space-y-4" data-testid="assessments-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-bold flex items-center"><DollarSign className="h-5 w-5 mr-2 text-amber-600" />Special Assessments</h3>
          <p className="text-xs text-gray-500">Create and track special assessments for repairs & improvements</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-assessment-btn">
          <Plus className="h-4 w-4 mr-1" /> New Assessment
        </Button>
      </div>

      {assessments.length === 0 ? (
        <Card><CardContent className="text-center py-8 text-sm text-gray-500">No assessments created yet.</CardContent></Card>
      ) : (
        <div className="grid gap-3">
          {assessments.map(a => {
            const totalPaid = (a.unit_assessments || []).reduce((s, u) => s + (u.paid || 0), 0);
            const pct = a.total_amount > 0 ? Math.round((totalPaid / a.total_amount) * 100) : 0;
            return (
              <Card key={a.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setSelectedAssessment(a); refreshSelected(a.id); }} data-testid={`assessment-card-${a.id}`}>
                <CardContent className="p-3 sm:p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-sm truncate">{a.title}</h4>
                        {statusBadge(a.status)}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 mt-1">
                        <span>{formatCurrency(a.total_amount)}</span>
                        <span>{(a.unit_assessments || []).length} units</span>
                        <span className="text-emerald-600">{pct}% collected</span>
                        {a.due_date && <span>Due: {a.due_date}</span>}
                      </div>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 hidden sm:block shrink-0" />
                  </div>
                  <div className="mt-2 w-full bg-gray-200 rounded-full h-1.5">
                    <div className="bg-emerald-500 h-1.5 rounded-full" style={{ width: `${pct}%` }} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-base">Create Special Assessment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title</Label><Input value={newAssessment.title} onChange={e => setNewAssessment({...newAssessment, title: e.target.value})} placeholder="e.g., Roof Repair Assessment" /></div>
            <div><Label className="text-xs">Description</Label><textarea className="w-full border rounded p-2 text-sm min-h-[60px]" value={newAssessment.description} onChange={e => setNewAssessment({...newAssessment, description: e.target.value})} placeholder="Details about the assessment..." /></div>
            <div><Label className="text-xs">Category</Label>
              <Select value={newAssessment.category} onValueChange={v => setNewAssessment({...newAssessment, category: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="repair">Repair</SelectItem>
                  <SelectItem value="improvement">Capital Improvement</SelectItem>
                  <SelectItem value="emergency">Emergency Repair</SelectItem>
                  <SelectItem value="legal">Legal/Insurance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Total Amount ($)</Label><Input type="number" step="0.01" value={newAssessment.total_amount} onChange={e => setNewAssessment({...newAssessment, total_amount: e.target.value})} placeholder="50000" /></div>
              <div><Label className="text-xs">Due Date</Label><Input type="date" value={newAssessment.due_date} onChange={e => setNewAssessment({...newAssessment, due_date: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button size="sm" onClick={createAssessment} disabled={!newAssessment.title || !newAssessment.total_amount}>Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HOAAssessments;
